// src/pages/ProbePage.tsx
import React, { useState, useEffect, useCallback } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faShareNodes } from '@fortawesome/free-solid-svg-icons';
import { useLocation, useNavigate } from 'react-router-dom';
import { LogRecord, ProbeData } from '../models/Probe';
import { fetchProbeData, fetchAvailableRequestIds} from '../services/probeService';
import { useCustomer } from '../contexts/CustomerContext';
import SectionHeader from '../components/common/SectionHeader';
import ProbeInput from '../components/probe/ProbeInput';
import ProbeHistory from '../components/probe/ProbeHistory';
import ProbeList from '../components/probe/ProbeList';
import ProbeTimeline from '../components/probe/ProbeTimeline';
import ProbeEventDetails from '../components/probe/ProbeEventDetails';
import logger from '../utils/logger';
import './ProbePage.css'; 
import { FetchState } from '../models/commonTypes';
import { NIL } from 'uuid';

const MAX_HISTORY_ITEMS = 10;

const TAZAPAY_SERVICES_TO_BE_FETCHED_FROM = [
  "settlement-orchestration-prod", "ruleengine-prod", "payin-prod", "payment-orchestration-prod", 
  "onboarding-prod", "metadata-prod", "refund-prod", "event-prod", "ops-dashboard-prod", "settlement-prod", 
  "fx-prod", "account-prod", "payment-router-prod", "mp-dashboard-prod", "payout-router-prod", 
  "notification-prod", "report-prod", "pricing-prod", "balance-prod", "monitoring-prod"
];


const ProbePage: React.FC = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { selectedCustomer, availableCustomers, selectCustomerById } = useCustomer();
  const [probeFetchState, setProbeFetchState] = useState<FetchState<ProbeData>>({ data: null, isLoading: false, error: null });
  const [selectedEventIndex, setSelectedEventIndex] = useState<number | null>(null);
  const [currentRequestId, setCurrentRequestId] = useState<string>(''); // State to hold the currently displayed/probed request ID
  const [history, setHistory] = useState<string[]>([]);
  const [notification, setNotification] = useState<string | null>(null);

  
  const [requestIdsState, setRequestIdsState] = useState<FetchState<string[]>>({
    data: [], isLoading: false, error: null,
  });
  const [filterAttribute, setFilterAttribute] = useState<string | null>(null);
  const [filterValue, setFilterValue] = useState<string | null>(null);
  const [services, setServices] = useState<string | null>(null);

  const { selectedApiKey} = useCustomer();
  const probeName = "request_id"

  const handleShare = useCallback(() => {
    const params = new URLSearchParams();
    if (selectedCustomer) {
      params.set('cid', selectedCustomer.id);
    }
    // Only add request_id if it's a valid one, not the default placeholder
    if (currentRequestId) {
      params.set('rid', currentRequestId);
    }
    if (filterAttribute) {
      params.set('fa', filterAttribute);
    }
    if (filterValue) {
      params.set('fv', filterValue);
    }
    if (services) {
      params.set('services', services);
    }

    const shareUrl = `${window.location.origin}${window.location.pathname}?${params.toString()}`;
    navigator.clipboard.writeText(shareUrl).then(() => {
      // alert('Probe link copied to clipboard!'); // Replace with a better notification component later
      setNotification('Probe link copied to clipboard!');
      setTimeout(() => setNotification(null), 3000); // Hide after 3 seconds    
    }).catch(err => logger.error('Failed to copy link: ', err));
  }, [selectedCustomer, currentRequestId, filterAttribute, filterValue, services]);

  const handleProbe = useCallback(async (requestId: string) => {
    if (!selectedCustomer) {
      setProbeFetchState({ data: null, isLoading: false, error: "No customer selected. Please select a team first."});
      return;
    }
    if (!selectedApiKey) {
      setProbeFetchState({ data: null, isLoading: false, error: "No customer selected for API Key. Please select a team first."});
      return;
    }
    // Do not probe if request ID is empty or just whitespace
    if (!requestId || !requestId.trim()) return;

    // Add to history if not already present and update order
    setHistory((prevHistory) => {
      const newHistory = [requestId, ...prevHistory.filter((id) => id !== requestId)];
      return newHistory.slice(0, MAX_HISTORY_ITEMS);
    });

    setProbeFetchState({ data: null, isLoading: true, error: null });
    setCurrentRequestId(requestId); // Update the input field with the probed ID
    setSelectedEventIndex(null);

    try {

      const servicesList = services ? services.split(',') : TAZAPAY_SERVICES_TO_BE_FETCHED_FROM;
      const data = await fetchProbeData(selectedApiKey, selectedCustomer.id, servicesList, requestId);
      setProbeFetchState({ data: data, isLoading: false, error: null });

      // Default selection: first ERROR event, otherwise first event
      const errorEventIndex = data.events.findIndex(event => (event.severity as string)?.toUpperCase() === 'ERROR');
      setSelectedEventIndex(errorEventIndex !== -1 ? errorEventIndex : (data.events.length > 0 ? 0 : null));

    } catch (err: any) {
      logger.error('Error fetching probe data:', err);
      setProbeFetchState({ data: null, isLoading: false, error: err.message || 'Failed to fetch probe data.'});
    }
  }, [selectedCustomer, selectedApiKey, services]);
    
  // Load history from localStorage on mount and parse URL params
  useEffect(() => {
    const storedHistory = localStorage.getItem('probeHistory');
    if (storedHistory) {
      setHistory(JSON.parse(storedHistory));
    }

    const queryParams = new URLSearchParams(location.search);
    const customerIdFromUrl = queryParams.get('cid');
    const idsParam = queryParams.get('request_ids');
    const probeRequestId = queryParams.get('rid');
    const currentFilterAttribute = queryParams.get('fa');
    const currentFilterValue = queryParams.get('fv');
    const currentServices = queryParams.get('services');

    setFilterAttribute(currentFilterAttribute);
    setFilterValue(currentFilterValue);
    setServices(currentServices);

    // If a customer_id is in the URL, it takes precedence. We must ensure it's selected.
    if (customerIdFromUrl && availableCustomers.length > 0) {
      if (!selectedCustomer || selectedCustomer.id !== customerIdFromUrl) {
        const customerToSelect = availableCustomers.find(c => c.id === customerIdFromUrl); // Use availableCustomers here
        if (customerToSelect) { // Use selectCustomerById to update the selected customer
          selectCustomerById(customerToSelect.id);
          // The component will re-render with the correct customer.
          // We return here to let the next run of useEffect handle the actual data fetching.
          return;
        } else {
          setProbeFetchState({ data: null, isLoading: true, error: `Customer with ID "${customerIdFromUrl}" not found or you don't have access.`});
        }
      }
    }

    if (probeRequestId && selectedCustomer) {
      handleProbe(probeRequestId);
    }

    if (idsParam && !probeRequestId) { // Don't load this list if we are already probing a specific ID from URL
      setRequestIdsState({ data: idsParam.split(',').map(id => id.trim()).filter(Boolean), isLoading: false, error: null });
    } else if (selectedCustomer && currentFilterAttribute && currentFilterValue) {
      // If no IDs are in the URL, fetch the list of available mock IDs from the manifest
      const loadAvailableIds = async () => {
        setRequestIdsState({ data: [], isLoading: true, error: null });
        const filters = currentFilterAttribute && currentFilterValue 
          ? [ { "attribute": currentFilterAttribute, "attribute_value": currentFilterValue }]
          : [];

        console.log( `services: ${currentServices}`)

        try {
          const servicesList = currentServices ? currentServices.split(',') : [];
          const ids = await fetchAvailableRequestIds(selectedApiKey, selectedCustomer?.id, servicesList, filters);
          setRequestIdsState({ data: ids, isLoading: false, error: null });
        } catch (err: any) {
          const errorMessage = err.message || 'Failed to load request IDs.';
          logger.error('Error loading request IDs:', err)
          setRequestIdsState({ data: [], isLoading: false, error: errorMessage });
        }
      };
      loadAvailableIds();
    } else { // No params, so no request IDs to show.
      // No params, so no request IDs to show. 
      setRequestIdsState({ data: [], isLoading: false, error: null });
    }
  }, [location.search, selectedCustomer, availableCustomers, selectCustomerById, selectedApiKey, handleProbe]);

  // Save history to localStorage whenever it changes
  useEffect(() => {
    localStorage.setItem('probeHistory', JSON.stringify(history));
  }, [history]);

  const handleSelectEvent = useCallback((index: number) => {
    setSelectedEventIndex(index);
  }, []);

  const handleRequestIdChange = useCallback((value: string) => {
    setCurrentRequestId(value);
  }, []);

  const handleSelectRequestId = (requestId: string) => {
    handleProbe(requestId);
  };

  const handleDeleteHistoryItem = useCallback((requestIdToDelete: string) => {
    setHistory((prevHistory) => prevHistory.filter((id) => id !== requestIdToDelete));
    // If the deleted item was the one currently displayed in the input, clear or reset the input/probeData.
    if (currentRequestId === requestIdToDelete) {
      // Clear displayed data if the current one is deleted
      setProbeFetchState({ data: null, isLoading: false, error: null});
      setSelectedEventIndex(null);
      // Optionally: setCurrentRequestId(''); // Uncomment to clear the input field too
    }
  }, [currentRequestId]); // Dependency on currentRequestId to check if the deleted item is the current one

  const selectedEvent = probeFetchState?.data?.events[selectedEventIndex!] || null;

  // If there's a critical error (like customer not found), show a full-page error.
  if (probeFetchState.error && probeFetchState.error.startsWith('Customer with ID') || probeFetchState.error?.startsWith( 'No customer selected.')) {
    return (
      <div className="probe-page-container flex items-center justify-center p-4">
        <div className="error-banner max-w-lg text-center">
          <h3 className="text-xl font-bold mb-2">Probe Unavailable</h3>
          <p className="text-base">{probeFetchState.error}</p>
          <p className="mt-4 text-sm text-text-dark">
            Please check the URL or select a valid team to continue.
          </p>
          <button
            onClick={() => navigate('/teams')}
            className="deriq-action-button mt-6"
          >
            Go to Teams Page
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="probe-page-container">
      <div className="probe-left-panel">
        <SectionHeader 
          title={"Probe"} 
          showBackButton={availableCustomers.length > 1}
          onBack={() => navigate('/teams')}
        />
        <ProbeInput probeName="Request ID" onProbe={handleProbe} isLoading={probeFetchState.isLoading} value={currentRequestId} onValueChange={handleRequestIdChange} placeholder="Enter Request ID..."/>
        <ProbeHistory history={history} onSelect={handleProbe} onDelete={handleDeleteHistoryItem} /> {/* Pass onDelete prop */}
        
        {filterAttribute && filterValue && (
          <div className="p-4 border-b border-border-dark">
            <h3 className="text-lg font-semibold mb-2 text-text-light">Active Filters</h3>
            <p className="text-sm text-text-dark break-all">
              <span className="font-semibold">{filterAttribute}:</span> {filterValue}
            </p>
            <p className="text-sm text-text-dark break-all">
              <span className="font-semibold">services:</span> {services}
            </p>
          </div>
        )}

        {requestIdsState.isLoading && (
          <div className="p-4 text-text-dark text-center">Loading request IDs...</div>
        )}
        {requestIdsState.error && (
          <div className="p-4 text-error-color text-center">{requestIdsState.error}</div>
        )}
        {!requestIdsState.isLoading && !requestIdsState.error && (
          <>
            {requestIdsState.data && requestIdsState.data.length > 0 ? (
              <ProbeList probeName={filterAttribute ? "Filtered Request IDs" : "Request IDs"} 
                probeIds={requestIdsState.data} onSelect={handleSelectRequestId} />
            ) : (
              filterAttribute && (
                <div className="p-4 text-text-dark text-center">No matching request IDs found for this filter.</div>
              )
            )}
          </>
        )}
      </div>
      <div className="probe-right-panel">
        {probeFetchState.isLoading && <div className="text-center p-4 text-text-light">Loading probe data...</div>}
        {probeFetchState.error && <div className="text-center p-4 text-error-color">{probeFetchState.error}</div>}
        {!probeFetchState.isLoading && !probeFetchState.error && !probeFetchState.data && (
          <div className="text-center p-4 text-text-dark">Enter a {probeName} to start probing.</div>
        )}
        {probeFetchState.data && (
          <>
            <div className="flex-shrink-0">
              <div className="flex justify-between items-center pr-4">
                <SectionHeader title={`Probe for request ID: ${currentRequestId}`} />
                <button onClick={handleShare} className="deriq-action-button-sm" title="Share Probe Link">
                  <FontAwesomeIcon icon={faShareNodes} />
                  <span className="ml-2 hidden sm:inline">Share</span>
                </button>
              </div>
            </div>
            <ProbeTimeline events={probeFetchState.data.events} selectedEventIndex={selectedEventIndex} onSelectEvent={handleSelectEvent} />
            <div className="flex-1 min-h-0"> {/* Wrapper to allow ProbeEventDetails to fill remaining space and have its own scroll */}
              <ProbeEventDetails 
                event={selectedEvent} 
                currentIndex={selectedEventIndex}
                totalEvents={probeFetchState.data.events.length}
                onNavigate={handleSelectEvent}
              />
            </div>
          </>
        )}
      </div>
     {notification && (
        <div className="probe-notification">
          {notification}
        </div>
      )}      
    </div>
  );
};

export default ProbePage;